import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  Grid,
  Button,
  Loader,
  Center,
  Alert,
  Modal,
  Select,
  FileButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconFileText,
  IconUser,
  IconPhone,
  IconMail,
  IconCalendar,
  IconMapPin,
  IconHeart,
  IconBriefcase,
  IconClock,
  IconLanguage,
  IconUserCheck,
  IconShield,
  IconCheck,
  IconBuilding,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useDeleteApprovedVolunteer,
  useUploadVolunteerContract,
  useUploadVolunteerHealthCertificate,
  useVolunteer,
  useUpdateUserRole,
} from '../../../hooks/useVolunteer';
import { BackLink } from '@/components/BackLink';
import { protectedVolunteerApi } from '../../../api/volunteer';

export default function VolunteerDetail() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { volunteerId } = useParams<{ volunteerId: string }>();
  const [searchParams] = useSearchParams();
  const cameFromPendingUsers = searchParams.get('from') === 'pending-users';
  const {
    data: volunteer,
    isLoading,
    error,
    refetch,
  } = useVolunteer(volunteerId!);
  const updateRoleMutation = useUpdateUserRole();
  const deleteVolunteerMutation = useDeleteApprovedVolunteer();
  const uploadContractMutation = useUploadVolunteerContract();
  const uploadHealthCertificateMutation = useUploadVolunteerHealthCertificate();
  const isMobile = useMediaQuery('(max-width: 600px)');
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleRoleChange = async (
    newRole: 'SUPER_ADMIN' | 'ADMIN' | 'VOLUNTEER'
  ) => {
    if (!volunteer?.user) return;

    try {
      await updateRoleMutation.mutateAsync({
        userId: volunteer.user.id,
        role: newRole,
      });
      notifications.show({
        title: t('admin.volunteerDetail.roleUpdated'),
        message: t('admin.volunteerDetail.roleUpdatedMessage'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      refetch();
    } catch {
      notifications.show({
        title: t('common.error'),
        message: t('admin.volunteerDetail.roleUpdateError'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const handleDeleteVolunteer = async () => {
    if (!volunteer?.user) return;

    try {
      await deleteVolunteerMutation.mutateAsync(volunteer.user.id);
      notifications.show({
        title: t('admin.volunteerDetail.delete.notifications.deleted'),
        message: t(
          'admin.volunteerDetail.delete.notifications.deletedMessage',
          {
            name: `${volunteer.firstName} ${volunteer.lastName}`,
          }
        ),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      navigate('/app/admin/volunteers');
    } catch {
      notifications.show({
        title: t('common.error'),
        message: t('admin.volunteerDetail.delete.notifications.error'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const uploadDocument = async (
    type: 'contract' | 'healthCertificate',
    file: File | null
  ) => {
    if (!file || !volunteerId) return;

    const mutation =
      type === 'contract'
        ? uploadContractMutation
        : uploadHealthCertificateMutation;

    try {
      await mutation.mutateAsync({ volunteerId, file });
      notifications.show({
        title: t('admin.volunteerDetail.upload.success'),
        message: t(`admin.volunteerDetail.upload.${type}Success`),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      await refetch();
    } catch {
      notifications.show({
        title: t('common.error'),
        message: t('admin.volunteerDetail.upload.error'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !volunteer) {
    return (
      <Container size="lg" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('common.error')}
          color="red"
        >
          {t('admin.volunteerDetail.error')}
        </Alert>
      </Container>
    );
  }

  const contractUrl = volunteer.contractSigned
    ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'}/antrag/${volunteer.id}/signed-contract`
    : null;
  const healthCertificateUrl = volunteer.healthCertificateUploaded
    ? protectedVolunteerApi.getHealthCertificateUrl(volunteer.id)
    : null;

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <BackLink
              to={
                cameFromPendingUsers
                  ? '/app/admin/pending-users'
                  : '/app/admin/volunteers'
              }
              label={
                cameFromPendingUsers
                  ? t('admin.pendingUsers.title')
                  : t('admin.volunteerDetail.backToList')
              }
            />
            <Title order={1}>
              {volunteer.firstName} {volunteer.lastName}
            </Title>
            <Text c="dimmed" size="lg">
              {volunteer.email}
            </Text>
          </div>
          <Group>
            <Badge
              color={volunteer.contractSigned ? 'green' : 'yellow'}
              variant="light"
              size="lg"
            >
              {volunteer.contractSigned
                ? t('admin.volunteers.status.signed')
                : t('admin.volunteers.status.pending')}
            </Badge>
            {volunteer.user && (
              <Badge color="blue" variant="light" size="lg">
                {t('admin.volunteers.status.hasAccount')}
              </Badge>
            )}
          </Group>
        </Group>

        <Grid gutter="lg">
          {/* Personal Information */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Title order={3} mb="md">
                {t('admin.volunteerDetail.sections.personal')}
              </Title>
              <Stack gap="sm">
                <Group gap="sm">
                  <IconUser size={18} color="gray" />
                  <Text>
                    {volunteer.firstName} {volunteer.lastName}
                  </Text>
                </Group>
                <Group gap="sm">
                  <IconMail size={18} color="gray" />
                  <Text>{volunteer.email}</Text>
                </Group>
                <Group gap="sm">
                  <IconPhone size={18} color="gray" />
                  <Text>{volunteer.phone}</Text>
                </Group>
                <Group gap="sm">
                  <IconCalendar size={18} color="gray" />
                  <Text>{formatDate(volunteer.birthDate)}</Text>
                </Group>
                {(volunteer.address || volunteer.city) && (
                  <Group gap="sm" align="flex-start">
                    <IconMapPin size={18} color="gray" />
                    <Text>
                      {[volunteer.address, volunteer.zipCode, volunteer.city]
                        .filter(Boolean)
                        .join(', ')}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Card>
          </Grid.Col>

          {/* Emergency Contact */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Title order={3} mb="md">
                {t('admin.volunteerDetail.sections.emergency')}
              </Title>
              {volunteer.emergencyContactName ? (
                <Stack gap="sm">
                  <Group gap="sm">
                    <IconUser size={18} color="gray" />
                    <Text>{volunteer.emergencyContactName}</Text>
                  </Group>
                  {volunteer.emergencyContactPhone && (
                    <Group gap="sm">
                      <IconPhone size={18} color="gray" />
                      <Text>{volunteer.emergencyContactPhone}</Text>
                    </Group>
                  )}
                  {volunteer.emergencyContactRelation && (
                    <Group gap="sm">
                      <IconUserCheck size={18} color="gray" />
                      <Text>{volunteer.emergencyContactRelation}</Text>
                    </Group>
                  )}
                </Stack>
              ) : (
                <Text c="dimmed">
                  {t('admin.volunteerDetail.noEmergencyContact')}
                </Text>
              )}
            </Card>
          </Grid.Col>

          {/* Volunteer Information */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Title order={3} mb="md">
                {t('admin.volunteerDetail.sections.volunteer')}
              </Title>
              <Stack gap="md">
                {volunteer.motivation && (
                  <div>
                    <Group gap="xs" mb={4}>
                      <IconHeart size={18} color="gray" />
                      <Text fw={500}>
                        {t('admin.volunteerDetail.fields.motivation')}
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {volunteer.motivation}
                    </Text>
                  </div>
                )}
                {volunteer.experience && (
                  <div>
                    <Group gap="xs" mb={4}>
                      <IconBriefcase size={18} color="gray" />
                      <Text fw={500}>
                        {t('admin.volunteerDetail.fields.experience')}
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {volunteer.experience}
                    </Text>
                  </div>
                )}
                {volunteer.department && (
                  <div>
                    <Group gap="xs" mb={4}>
                      <IconBuilding size={18} color="gray" />
                      <Text fw={500}>
                        {t('admin.volunteerDetail.fields.department')}
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {volunteer.department
                        .map(dept => t(`departments.${dept.toLowerCase()}`))
                        .join(', ')}
                    </Text>
                  </div>
                )}
                {volunteer.weekdaysAvailable && (
                  <div>
                    <Group gap="xs" mb={4}>
                      <IconCalendar size={18} color="gray" />
                      <Text fw={500}>
                        {t('admin.volunteerDetail.fields.weekdaysAvailable')}
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {volunteer.weekdaysAvailable
                        .map(day => t(`weekdays.${day.toLowerCase()}`))
                        .join(', ')}
                    </Text>
                  </div>
                )}
                {volunteer.workingHoursPerMonth && (
                  <div>
                    <Group gap="xs" mb={4}>
                      <IconClock size={18} color="gray" />
                      <Text fw={500}>
                        {t('admin.volunteerDetail.fields.workingHoursPerMonth')}
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {t('admin.volunteerDetail.fields.hoursPerMonth', {
                        hours: volunteer.workingHoursPerMonth,
                      })}
                    </Text>
                  </div>
                )}
                {volunteer.languages && (
                  <div>
                    <Group gap="xs" mb={4}>
                      <IconLanguage size={18} color="gray" />
                      <Text fw={500}>
                        {t('admin.volunteerDetail.fields.languages')}
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {volunteer.languages}
                    </Text>
                  </div>
                )}
                {!volunteer.motivation &&
                  !volunteer.experience &&
                  !volunteer.department &&
                  !volunteer.weekdaysAvailable &&
                  !volunteer.workingHoursPerMonth &&
                  !volunteer.languages && (
                    <Text c="dimmed">
                      {t('admin.volunteerDetail.noVolunteerInfo')}
                    </Text>
                  )}
              </Stack>
            </Card>
          </Grid.Col>

          {/* Contract & Account Status */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Title order={3} mb="md">
                {t('admin.volunteerDetail.sections.status')}
              </Title>
              <Stack gap="md">
                <div>
                  <Text fw={500} mb={4}>
                    {t('admin.volunteerDetail.fields.contractStatus')}
                  </Text>
                  <Group gap="sm">
                    <Badge
                      color={volunteer.contractSigned ? 'green' : 'yellow'}
                      variant="light"
                    >
                      {volunteer.contractSigned
                        ? t('admin.volunteers.status.signed')
                        : t('admin.volunteers.status.pending')}
                    </Badge>
                    {volunteer.contractSignedAt && (
                      <Text size="sm" c="dimmed">
                        {formatDate(volunteer.contractSignedAt)}
                      </Text>
                    )}
                  </Group>
                  {volunteer.contractSigned &&
                    contractUrl &&
                    (isMobile ? (
                      <Button
                        component="a"
                        href={contractUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="light"
                        size="xs"
                        mt="sm"
                        leftSection={<IconFileText size={14} />}
                      >
                        {t('admin.volunteerDetail.viewContract')}
                      </Button>
                    ) : (
                      <Button
                        variant="light"
                        size="xs"
                        mt="sm"
                        leftSection={<IconFileText size={14} />}
                        onClick={() => setContractModalOpen(true)}
                      >
                        {t('admin.volunteerDetail.viewContract')}
                      </Button>
                    ))}
                  <FileButton
                    onChange={file => void uploadDocument('contract', file)}
                    accept="application/pdf"
                  >
                    {props => (
                      <Button
                        {...props}
                        variant="light"
                        size="xs"
                        mt="sm"
                        ml={volunteer.contractSigned ? 'xs' : 0}
                        leftSection={<IconUpload size={14} />}
                        loading={uploadContractMutation.isPending}
                      >
                        {t('admin.volunteerDetail.upload.contract')}
                      </Button>
                    )}
                  </FileButton>
                </div>

                <div>
                  <Text fw={500} mb={4}>
                    {t('admin.volunteerDetail.fields.accountStatus')}
                  </Text>
                  <Badge
                    color={volunteer.user ? 'blue' : 'gray'}
                    variant="light"
                  >
                    {volunteer.user
                      ? t('admin.volunteers.status.hasAccount')
                      : t('admin.volunteers.status.noAccount')}
                  </Badge>
                  {volunteer.user && (
                    <Text size="sm" c="dimmed" mt={4}>
                      {t('admin.volunteerDetail.accountCreated', {
                        date: formatDate(volunteer.user.createdAt),
                      })}
                    </Text>
                  )}
                </div>

                <div>
                  <Text fw={500} mb={4}>
                    {t('admin.volunteerDetail.fields.healthCertificateStatus')}
                  </Text>
                  <Group gap="sm">
                    <Badge
                      color={
                        volunteer.healthCertificateReviewStatus === 'APPROVED'
                          ? 'green'
                          : volunteer.healthCertificateReviewStatus ===
                              'REJECTED'
                            ? 'red'
                            : 'yellow'
                      }
                      variant="light"
                    >
                      {volunteer.healthCertificateReviewStatus === 'APPROVED'
                        ? t('profile.documents.approved')
                        : volunteer.healthCertificateReviewStatus === 'PENDING'
                          ? t('profile.documents.pending')
                          : volunteer.healthCertificateReviewStatus ===
                              'REJECTED'
                            ? t('profile.documents.rejected')
                            : t(
                                'admin.volunteerDetail.healthCertificateMissing'
                              )}
                    </Badge>
                    {volunteer.healthCertificateUploadedAt && (
                      <Text size="sm" c="dimmed">
                        {formatDate(volunteer.healthCertificateUploadedAt)}
                      </Text>
                    )}
                  </Group>
                  {volunteer.healthCertificateOriginalName && (
                    <Text size="sm" c="dimmed" mt={4}>
                      {volunteer.healthCertificateOriginalName}
                    </Text>
                  )}
                  {healthCertificateUrl && (
                    <Button
                      component="a"
                      href={healthCertificateUrl}
                      target="_blank"
                      rel="noreferrer"
                      variant="light"
                      size="xs"
                      mt="sm"
                      leftSection={<IconFileText size={14} />}
                    >
                      {t('admin.volunteerDetail.viewHealthCertificate')}
                    </Button>
                  )}
                  <FileButton
                    onChange={file =>
                      void uploadDocument('healthCertificate', file)
                    }
                    accept="application/pdf,image/jpeg,image/png"
                  >
                    {props => (
                      <Button
                        {...props}
                        variant="light"
                        size="xs"
                        mt="sm"
                        ml={healthCertificateUrl ? 'xs' : 0}
                        leftSection={<IconUpload size={14} />}
                        loading={uploadHealthCertificateMutation.isPending}
                      >
                        {t('admin.volunteerDetail.upload.healthCertificate')}
                      </Button>
                    )}
                  </FileButton>
                </div>

                {volunteer.user && (
                  <div>
                    <Group gap="xs" mb={4}>
                      <IconShield size={18} color="gray" />
                      <Text fw={500}>
                        {t('admin.volunteerDetail.fields.role')}
                      </Text>
                    </Group>
                    <Select
                      value={volunteer.user.role}
                      onChange={value =>
                        value &&
                        handleRoleChange(
                          value as 'SUPER_ADMIN' | 'ADMIN' | 'VOLUNTEER'
                        )
                      }
                      data={[
                        {
                          value: 'VOLUNTEER',
                          label: t('admin.volunteerDetail.roles.volunteer'),
                        },
                        {
                          value: 'ADMIN',
                          label: t('admin.volunteerDetail.roles.admin'),
                        },
                        {
                          value: 'SUPER_ADMIN',
                          label: t('admin.volunteerDetail.roles.superAdmin'),
                        },
                      ]}
                      disabled={updateRoleMutation.isPending}
                      size="sm"
                      w={200}
                    />
                  </div>
                )}

                <div>
                  <Text fw={500} mb={4}>
                    {t('admin.volunteerDetail.fields.registeredAt')}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {formatDate(volunteer.createdAt)}
                  </Text>
                </div>

                {volunteer.user?.approved &&
                  volunteer.user.role === 'VOLUNTEER' && (
                    <div>
                      <Button
                        color="red"
                        variant="light"
                        leftSection={<IconTrash size={16} />}
                        onClick={() => setDeleteModalOpen(true)}
                      >
                        {t('admin.volunteerDetail.delete.action')}
                      </Button>
                    </div>
                  )}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>

      {/* Contract PDF Modal */}
      <Modal
        opened={contractModalOpen}
        onClose={() => setContractModalOpen(false)}
        title={t('admin.volunteerDetail.contractModalTitle')}
        size="xl"
        styles={{
          body: { padding: 0, height: '80vh' },
          content: { height: '85vh' },
        }}
      >
        {contractUrl && (
          <iframe
            src={contractUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title={t('admin.volunteerDetail.contractModalTitle')}
          />
        )}
      </Modal>

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t('admin.volunteerDetail.delete.dialog.title')}
        centered
      >
        <Stack>
          <Text>
            {t('admin.volunteerDetail.delete.dialog.message', {
              name: `${volunteer.firstName} ${volunteer.lastName}`,
            })}
          </Text>
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {t('admin.volunteerDetail.delete.dialog.details')}
          </Alert>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleteVolunteerMutation.isPending}
            >
              {t('admin.volunteerDetail.delete.dialog.cancel')}
            </Button>
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleDeleteVolunteer}
              loading={deleteVolunteerMutation.isPending}
            >
              {t('admin.volunteerDetail.delete.dialog.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
